import React from 'react';
import cls from 'classnames';
import {
  useEnvironment,
  useTranslation,
  useExperiments,
} from '@wix/yoshi-flow-editor';
import { useSettings, useStyles } from '@wix/tpa-settings/react';

import { EAlignment, ELayoutType } from 'settings/constants';

import stylesParams from 'Groups/stylesParams';
import settingsParams from 'Groups/settingsParams';

import { Search as SearchIcon } from '@wix/wix-ui-icons-common/on-stage';

import { TextButton } from 'wui/TextButton';
import { Box } from 'wui/Box';
import { Show } from 'wui/Show';
import { Typography } from 'wui/Typography';
import { type TPAComponentProps } from 'wui/types';
import { IconButton } from 'wui/IconButton';

import { CentralFeed } from '../../CentralFeed/loadable';
import { PendingGroups } from '../../PendingGroups';
import { Search } from '../../Search';

import { Sidebar } from './Sidebar';
import { GroupsDialog } from './GroupsDialog';

import { GROUP_LIST_LINK_DATA_HOOK } from './dataHooks';

import classes from './SidebarLayout.scss';

export function SidebarLayout(props: TPAComponentProps) {
  const { className, ...rest } = props;

  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();
  const settings = useSettings();
  const styles = useStyles();

  const { htmlTag: htmlTitleTag } = styles.get(stylesParams.feedTitleFont);
  const { htmlTag: htmlSubtitleTag } = styles.get(
    stylesParams.feedSubtitleFont,
  );

  const [isGroupPickerOpened, setIsGroupPickerOpened] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);

  return (
    <div
      data-type={ELayoutType.Sidebar}
      className={cls(classes.root, className, {
        [classes.mobile]: isMobile,
        [classes.flip]:
          styles.get(stylesParams.sideBarAlignment) === EAlignment.Left,
      })}
      {...rest}
    >
      <Show if={isMobile}>
        <Box padding={isMobile ? 'SP0 SP5' : 'SP0'} direction="vertical">
          <Box gap="SP2" align="space-between" className={classes.header}>
            <Typography
              as={htmlTitleTag as React.ElementType}
              variant="h2-20"
              id="feed-title"
              className={classes.title}
            >
              {settings.get(settingsParams.feedTitle)}
            </Typography>

            <Box gap="SP5">
              <TextButton
                data-hook={GROUP_LIST_LINK_DATA_HOOK}
                onClick={handleClick}
              >
                {t('groups-web.group-list.label')}
              </TextButton>
              <IconButton
                data-hook="search-button"
                icon={<SearchIcon />}
                onClick={() => setIsSearchOpen(true)}
              />
            </Box>

            <Search
              isOpen={isSearchOpen}
              onClose={() => setIsSearchOpen(false)}
              origin="central_feed"
            />
            <GroupsDialog
              isOpen={isGroupPickerOpened}
              onClose={() => setIsGroupPickerOpened(false)}
            />
          </Box>
          <Typography
            as={htmlSubtitleTag as React.ElementType}
            variant="p2-16"
            id="feed-subtitle"
            className={classes.subtitle}
          >
            {settings.get(settingsParams.feedSubtitle)}
          </Typography>
        </Box>
      </Show>

      <Show if={!isMobile}>
        <Box
          gap="SP2"
          direction="vertical"
          align="space-between"
          className={classes.header}
          padding={isMobile ? 'SP0 SP5' : 'SP0'}
        >
          <Typography
            as={htmlTitleTag as React.ElementType}
            variant="h2-20"
            id="feed-title"
            className={classes.title}
          >
            {settings.get(settingsParams.feedTitle)}
          </Typography>

          <Typography
            as={htmlSubtitleTag as React.ElementType}
            variant="p2-16"
            id="feed-subtitle"
            className={classes.subtitle}
          >
            {settings.get(settingsParams.feedSubtitle)}
          </Typography>
        </Box>

        <div className={classes.sidebar}>
          <Sidebar />
        </div>
      </Show>

      <Box
        direction="vertical"
        className={classes.main}
        gap={styles.get(stylesParams.sideBarVerticalSpacing)}
      >
        <PendingGroups />
        <CentralFeed />
      </Box>
    </div>
  );

  function handleClick() {
    setIsGroupPickerOpened(true);
  }
}

SidebarLayout.displayName = 'SidebarLayout';
